import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { BellRing, Calculator, KeyRound, Pause, Play, Plus, Send } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
  type Tone,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  PushAudience,
  PushCampaign,
  PushPreview,
  PushReport,
  PushSettings,
  SegmentRow,
  StaffDevice,
  StaffPrefs,
} from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Pazarlama → Anlık bildirimler (F6-10, ADR-091): kurulum (VAPID anahtarı, vitrin düğmesi, iletişim adresi,
 * aydınlatma metni, Firebase), abone sayısı, kampanyalar (başlık, metin, bağlantı, kitle, önizleme, uzman onayı,
 * MFA + Idempotency-Key ile gönder/zamanla, ilerleme, durdur/sürdür, rapor). Hesabım → Mobil bildirimler bölümü
 * de buradadır (cihazlar, tercihler, deneme bildirimi).
 */

type Status = PushCampaign['status'];
const statusTone: Record<Status, Tone> = {
  draft: 'neutral',
  scheduled: 'slate',
  sending: 'pine',
  sent: 'moss',
  paused: 'rose',
  cancelled: 'slate',
};
const LIVE: Status[] = ['scheduled', 'sending'];
const ROUTE = '/anlik-bildirimler';
const KNOWN_ERRORS = [
  'push_404',
  'push_410',
  'push_403',
  'push_too_large',
  'push_unavailable',
  'interrupted',
];
const PAUSE_KEYS = ['template_review_required', 'web_push_not_configured', 'push_auth_failed'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const pad = (n: number) => `${String(n).padStart(2, '0')}:00`;

function toLocalInput(v: string | Date | null): string {
  if (!v) return '';
  const d = new Date(v);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setV(value), ms);
    return () => clearTimeout(h);
  }, [value, ms]);
  return v;
}

function usePushSettings() {
  return useQuery({
    queryKey: ['push-settings'],
    queryFn: () => api.get<PushSettings>('/v1/admin/push/settings'),
  });
}

// ---------------------------------------------------------------------------- Liste ve kurulum

export function PushPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const settings = usePushSettings();
  const list = useQuery({
    queryKey: ['push-campaigns'],
    queryFn: () => api.get<PushCampaign[]>('/v1/admin/push-campaigns'),
    refetchInterval: (q) => (q.state.data?.some((c) => LIVE.includes(c.status)) ? 5000 : false),
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const rows = (list.data ?? []).filter((c) => c.storeId === storeId);
  return (
    <PageFrame
      helpKey="page.push"
      actions={
        can('marketing.campaign.write') &&
        storeId && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void navigate({ to: `${ROUTE}/$id`, params: { id: 'yeni' } })}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.push.create')}
            </Button>
            <HelpTip k="push.create" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <div className="space-y-6">
          <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.push.lead')}</p>
          {settings.data && <SetupSection settings={settings.data} />}
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {settings.data && storeId && (
            <p className="text-sm text-ink-2" aria-live="polite">
              {t('admin.push.subscriberCount', { count: settings.data.subscribers[storeId] ?? 0 })}
            </p>
          )}
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !rows.length ? (
            <EmptyState title={t('admin.push.emptyTitle')} description={t('admin.push.emptyLead')} />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm" aria-label={t('admin.push.listLabel')}>
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.push.colName')}</th>
                    <th className="px-3 py-2 text-start font-medium">{t('admin.push.colStatus')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.push.colSent')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('admin.push.colWhen')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} className="border-t border-line align-top">
                      <td className="px-3 py-2">
                        <Link
                          to={`${ROUTE}/$id`}
                          params={{ id: c.id }}
                          className="font-medium text-pine hover:underline"
                        >
                          {c.name}
                        </Link>
                        <p className="text-ink-3">{c.title}</p>
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={statusTone[c.status]}>{t(`admin.push.statuses.${c.status}`)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {c.progress ? `${c.progress.sent} / ${c.progress.total}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-end text-ink-2">
                        {formatDate(c.finishedAt ?? c.startedAt ?? c.scheduledAt ?? c.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </PageFrame>
  );
}

function SetupSection({ settings }: { settings: PushSettings }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canManage = can('automation.manage');
  const [enabled, setEnabled] = useState(settings.webPushEnabled);
  const [contact, setContact] = useState(settings.contact ?? settings.suggestedContact ?? '');
  useEffect(() => {
    setEnabled(settings.webPushEnabled);
    setContact(settings.contact ?? settings.suggestedContact ?? '');
  }, [settings]);
  const dirty = enabled !== settings.webPushEnabled || (contact.trim() || null) !== settings.contact;
  const createKey = useApiMutation((replace: boolean) => api.post('/v1/admin/push/vapid-keys', { replace }), {
    invalidate: [['push-settings']],
    success: t('admin.push.keyCreated'),
  });
  const save = useApiMutation(
    () => api.put('/v1/admin/push/settings', { webPushEnabled: enabled, contact: contact.trim() || null }),
    { invalidate: [['push-settings']], success: t('admin.push.settingsSaved') },
  );
  const setup = <Badge tone="amber">{t('integrationStatus.NOT_CONFIGURED')}</Badge>;
  const link = 'text-pine hover:underline';
  return (
    <Section
      title={t('admin.push.setupTitle')}
      description={t('admin.push.setupLead')}
      action={<HelpTip k="push.setup" />}
      className="border-t-0 pt-0"
    >
      <div className="space-y-5">
        <KeyValue
          items={[
            {
              label: t('admin.push.vapidKey'),
              value: settings.publicKey ? (
                <span className="flex flex-wrap items-center gap-2">
                  <Badge tone="moss">
                    {t('admin.push.keyReady', { date: formatDate(settings.keyCreatedAt) })}
                  </Badge>
                  {canManage && (
                    <>
                      <ConfirmButton
                        variant="secondary"
                        message={t('admin.push.replaceConfirm')}
                        onConfirm={() => createKey.mutateAsync(true).catch(() => undefined)}
                      >
                        {t('admin.push.replaceKey')}
                      </ConfirmButton>
                      <HelpTip k="push.replace_key" />
                    </>
                  )}
                </span>
              ) : (
                <span className="flex flex-wrap items-center gap-2">
                  {setup}
                  {canManage && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => createKey.mutate(false)}
                        disabled={createKey.isPending}
                      >
                        <KeyRound className="h-4 w-4" aria-hidden />
                        {t('admin.push.createKey')}
                      </Button>
                      <HelpTip k="push.create_key" />
                    </>
                  )}
                </span>
              ),
            },
            {
              label: t('admin.push.privacyNotice'),
              value: settings.privacyNotice ? (
                <Badge tone="moss">{t('admin.push.published')}</Badge>
              ) : (
                <span className="flex flex-wrap items-center gap-2">
                  <Badge tone="amber">{t('admin.push.missing')}</Badge>
                  <Link to="/yasal-metinler" className={link}>
                    {t('admin.push.privacyLink')}
                  </Link>
                </span>
              ),
            },
            {
              label: t('admin.push.android'),
              value: settings.firebaseConnected ? (
                <Badge tone="moss">{t('admin.push.connected')}</Badge>
              ) : (
                <span className="flex flex-wrap items-center gap-2">
                  {setup}
                  <Link to="/entegrasyonlar" className={link}>
                    {t('admin.push.firebaseLink')}
                  </Link>
                </span>
              ),
            },
          ]}
        />
        <fieldset disabled={!canManage} className="grid max-w-2xl gap-4">
          <legend className="sr-only">{t('admin.push.settingsTitle')}</legend>
          <Field
            label={t('admin.push.contact')}
            description={t('admin.push.contactLead')}
            help={<HelpTip k="push.contact" />}
            error={fieldError(save.error, 'contact')}
          >
            {(p) => (
              <Input
                {...p}
                value={contact}
                maxLength={200}
                placeholder="mailto:destek@ornek.com.tr"
                onChange={(e) => setContact(e.target.value)}
              />
            )}
          </Field>
          <div className="flex items-center gap-2">
            <Switch id="push-enabled" checked={enabled} onCheckedChange={setEnabled} disabled={!canManage} />
            <label htmlFor="push-enabled" className="text-sm font-medium">
              {t('admin.push.enabled')}
            </label>
            <HelpTip k="push.enabled" />
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <Button onClick={() => save.mutate(undefined)} disabled={!dirty || save.isPending}>
                {t('admin.push.saveSettings')}
              </Button>
              <HelpTip k="push.settings_save" />
            </div>
          )}
        </fieldset>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------- Kampanya düzenleyici

interface Draft {
  name: string;
  title: string;
  body: string;
  url: string;
  segmentIds: string[];
}
const emptyDraft = (): Draft => ({ name: '', title: '', body: '', url: '/', segmentIds: [] });
const draftOf = (c: PushCampaign): Draft => ({
  name: c.name,
  title: c.title,
  body: c.body,
  url: c.url,
  segmentIds: c.segmentIds,
});

export function PushCampaignEditorPage() {
  const { id } = useParams({ from: '/app/anlik-bildirimler/$id' });
  const isNew = id === 'yeni';
  const t = useT();
  const q = useQuery({
    queryKey: ['push-campaign', id],
    queryFn: () => api.get<PushCampaign>(`/v1/admin/push-campaigns/${id}`),
    enabled: !isNew,
    refetchInterval: (s) => (s.state.data && LIVE.includes(s.state.data.status) ? 5000 : false),
  });
  if (!isNew && !q.data) return <Spinner label={t('common.loading')} />;
  return <CampaignEditor key={q.data?.id ?? 'yeni'} campaign={q.data ?? null} />;
}

function CampaignEditor({ campaign }: { campaign: PushCampaign | null }) {
  const t = useT();
  const { formatDate } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('marketing.campaign.write');
  const canSend = can('marketing.campaign.send');
  const { stores, storeId: chosenStore, setStoreId } = useStoreChoice();
  const storeId = campaign?.storeId ?? chosenStore;
  const storeName = stores.find((s) => s.id === storeId)?.name ?? '';
  const editable = canWrite && (!campaign || campaign.status === 'draft');
  const settings = usePushSettings();
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<SegmentRow[]>('/v1/admin/segments'),
    enabled: can('marketing.audience.read'),
  });
  const [draft, setDraft] = useState<Draft>(() => (campaign ? draftOf(campaign) : emptyDraft()));
  useEffect(() => {
    if (campaign) setDraft(draftOf(campaign));
  }, [campaign]);
  const set = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));
  const dirty = !campaign || JSON.stringify(draft) !== JSON.stringify(draftOf(campaign));
  const complete =
    draft.name.trim().length >= 2 && draft.title.trim().length >= 2 && draft.body.trim().length >= 2;
  const ready = !!settings.data?.webPushEnabled && !!settings.data.publicKey;

  const previewKey = useDebounced(JSON.stringify({ title: draft.title, body: draft.body }), 400);
  const preview = useQuery({
    queryKey: ['push-preview', previewKey],
    queryFn: () =>
      api.post<PushPreview>('/v1/admin/push-campaigns/preview', JSON.parse(previewKey) as object),
    enabled: !!draft.title.trim() || !!draft.body.trim(),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const save = useApiMutation(
    () =>
      campaign
        ? api.patch<PushCampaign>(`/v1/admin/push-campaigns/${campaign.id}`, {
            ...draft,
            version: campaign.version,
          })
        : api.post<PushCampaign>('/v1/admin/push-campaigns', { ...draft, storeId }),
    {
      invalidate: [['push-campaigns'], ...(campaign ? [['push-campaign', campaign.id]] : [])],
      success: t('admin.push.saved'),
      onSuccess: (r) => {
        if (!campaign) void navigate({ to: `${ROUTE}/$id`, params: { id: r.id } });
      },
    },
  );
  const [estimate, setEstimate] = useState<PushAudience | null>(null);
  const estimateM = useApiMutation(
    () =>
      api.post<PushAudience>('/v1/admin/push-campaigns/audience', { storeId, segmentIds: draft.segmentIds }),
    { onSuccess: (r) => setEstimate(r) },
  );
  useEffect(() => setEstimate(null), [draft.segmentIds, storeId]);
  const review = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<PushCampaign>(`/v1/admin/push-campaigns/${campaign!.id}/review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: campaign!.review.contentHash,
        version: campaign!.version,
      }),
    {
      invalidate: [['push-campaigns'], ['push-campaign', campaign?.id]],
      success: t('admin.pages.reviewedToast'),
    },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/push-campaigns/${campaign!.id}`), {
    invalidate: [['push-campaigns']],
    success: t('admin.push.deleted'),
    onSuccess: () => void navigate({ to: ROUTE }),
  });
  const cancel = useApiMutation(
    () =>
      api.post<PushCampaign>(`/v1/admin/push-campaigns/${campaign!.id}/cancel`, {
        version: campaign!.version,
      }),
    { invalidate: [['push-campaigns'], ['push-campaign', campaign?.id]], success: t('admin.push.cancelled') },
  );
  const resumeIdem = useRef(new IdempotencyKey());
  const resume = useApiMutation(
    () => {
      const b = { version: campaign!.version };
      return api.post<PushCampaign>(`/v1/admin/push-campaigns/${campaign!.id}/resume`, b, {
        idempotencyKey: resumeIdem.current.for(JSON.stringify(b)),
      });
    },
    { invalidate: [['push-campaigns'], ['push-campaign', campaign?.id]], success: t('admin.push.resumed') },
  );
  const fe = (f: string) => fieldError(save.error, f);
  const findings = preview.data?.findings ?? [];

  return (
    <PageFrame
      helpKey="page.push_editor"
      title={campaign ? campaign.name : t('admin.push.create')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {editable && (
            <>
              <Button
                variant={campaign ? 'secondary' : 'primary'}
                onClick={() => save.mutate(undefined)}
                disabled={!dirty || !complete || !storeId || save.isPending}
              >
                {campaign ? t('admin.push.save') : t('admin.push.createConfirm')}
              </Button>
              <HelpTip k="push.save" />
            </>
          )}
          {campaign?.status === 'draft' && canSend && (
            <SendButton campaign={campaign} dirty={dirty} ready={ready} />
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to={ROUTE} className="text-pine hover:underline">
          {t('admin.push.back')}
        </Link>
        {campaign && (
          <Badge tone={statusTone[campaign.status]}>{t(`admin.push.statuses.${campaign.status}`)}</Badge>
        )}
        {campaign && (campaign.review.required || campaign.review.status !== 'not_required') && (
          <Badge tone={reviewTone[campaign.review.status]}>
            {t(`admin.pages.reviews.${campaign.review.status}`)}
          </Badge>
        )}
        {campaign && editable && dirty && <Badge tone="amber">{t('admin.push.unsaved')}</Badge>}
        {settings.data && !ready && <Badge tone="amber">{t('admin.push.notReady')}</Badge>}
      </div>
      {!campaign && <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />}

      {campaign && campaign.status !== 'draft' && (
        <StatusSection
          campaign={campaign}
          canSend={canSend}
          onCancel={() => cancel.mutateAsync(undefined).catch(() => undefined)}
          onResume={() => resume.mutate(undefined)}
          busy={cancel.isPending || resume.isPending}
        />
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <fieldset disabled={!editable} className="grid content-start gap-4">
          <legend className="sr-only">{t('admin.push.editor')}</legend>
          <Field
            label={t('admin.push.name')}
            description={t('admin.push.nameLead')}
            help={<HelpTip k="push.name" />}
            error={fe('name')}
            required
          >
            {(p) => (
              <Input
                {...p}
                value={draft.name}
                maxLength={120}
                onChange={(e) => set({ name: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.push.title')}
            description={t('admin.push.counter', { n: draft.title.length, max: 65 })}
            help={<HelpTip k="push.title" />}
            error={fe('title')}
            required
          >
            {(p) => (
              <Input
                {...p}
                value={draft.title}
                maxLength={65}
                onChange={(e) => set({ title: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.push.body')}
            description={t('admin.push.counter', { n: draft.body.length, max: 180 })}
            help={<HelpTip k="push.body" />}
            error={fe('body')}
            required
          >
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={draft.body}
                maxLength={180}
                onChange={(e) => set({ body: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.push.url')}
            description={t('admin.push.urlLead')}
            help={<HelpTip k="push.url" />}
            error={fe('url')}
          >
            {(p) => (
              <Input
                {...p}
                value={draft.url}
                maxLength={500}
                onChange={(e) => set({ url: e.target.value })}
              />
            )}
          </Field>
          <fieldset className="space-y-1.5">
            <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
              {t('admin.push.audience')}
              <HelpTip k="push.audience" />
            </legend>
            <p className="text-sm text-ink-3">{t('admin.push.audienceLead')}</p>
            {(segments.data ?? []).map((sg) => (
              <label key={sg.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={draft.segmentIds.includes(sg.id)}
                  onChange={(e) =>
                    set({
                      segmentIds: e.target.checked
                        ? [...draft.segmentIds, sg.id]
                        : draft.segmentIds.filter((x) => x !== sg.id),
                    })
                  }
                />
                {sg.name}
              </label>
            ))}
            {fe('segmentIds') && (
              <p role="alert" className="text-sm font-medium text-rose">
                {fe('segmentIds')}
              </p>
            )}
          </fieldset>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() => estimateM.mutate(undefined)}
              disabled={!storeId || estimateM.isPending}
            >
              <Calculator className="h-4 w-4" aria-hidden />
              {t('admin.push.estimate')}
            </Button>
            <HelpTip k="push.estimate" />
          </div>
          {estimate && <EstimateView estimate={estimate} />}
        </fieldset>

        <section aria-labelledby="push-preview-title" className="space-y-3">
          <h2 id="push-preview-title" className="flex items-center gap-1 font-medium">
            {t('admin.push.preview')}
            <HelpTip k="push.preview" />
          </h2>
          <div className="flex gap-3 rounded-lg border border-line bg-surface p-3 shadow-sm">
            <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-pine" aria-hidden />
            <div className="min-w-0 text-sm">
              <p className="text-ink-3">{storeName}</p>
              <p className="font-semibold break-words">{draft.title || t('admin.push.previewTitle')}</p>
              <p className="break-words text-ink-2">{draft.body || t('admin.push.previewBody')}</p>
            </div>
          </div>
          <p className="text-sm text-ink-3">{t('admin.push.previewNote')}</p>
          {findings.length > 0 && (
            <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2 text-sm">
              {t('admin.push.findingsNote', { count: findings.length })}
            </p>
          )}
        </section>
      </div>

      {campaign && (
        <ContentReviewPanel
          status={campaign.review.status}
          note={campaign.review.note}
          findings={campaign.review.findings}
          required={campaign.review.required}
          dirty={dirty}
          canReview={can('content.expert_review')}
          busy={review.isPending}
          onDecide={(decision, note) => review.mutate({ decision, note })}
        />
      )}

      {campaign?.startedAt && <ReportSection campaign={campaign} />}

      {campaign &&
        canWrite &&
        (campaign.status === 'draft' || (campaign.status === 'cancelled' && !campaign.startedAt)) && (
          <Section title={t('admin.push.dangerZone')}>
            <div className="flex items-center gap-2">
              <ConfirmButton
                message={t('admin.push.deleteConfirm', { name: campaign.name })}
                onConfirm={() => remove.mutateAsync(undefined).catch(() => undefined)}
              >
                {t('common.delete')}
              </ConfirmButton>
              <HelpTip k="push.delete" />
            </div>
          </Section>
        )}
      {campaign && (
        <p className="text-sm text-ink-3">
          {t('admin.push.updated', { date: formatDate(campaign.updatedAt) })}
        </p>
      )}
    </PageFrame>
  );
}

function EstimateView({ estimate }: { estimate: PushAudience }) {
  const t = useT();
  return (
    <div className="rounded-md border border-line bg-surface p-3 text-sm" aria-live="polite">
      <p className="font-medium">
        {t('admin.push.estimateResult', { eligible: estimate.eligible, total: estimate.subscribers })}
      </p>
      {estimate.anonymousExcluded > 0 && (
        <p className="mt-1 text-ink-2">
          {t('admin.push.anonymousExcluded', { count: estimate.anonymousExcluded })}
        </p>
      )}
      <p className="mt-1 text-ink-3">{t('admin.push.estimateNote')}</p>
    </div>
  );
}

function SendButton({ campaign, dirty, ready }: { campaign: PushCampaign; dirty: boolean; ready: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [later, setLater] = useState(false);
  const [at, setAt] = useState(() => toLocalInput(new Date(Date.now() + 3_600_000)));
  const [estimate, setEstimate] = useState<PushAudience | null>(null);
  const idem = useRef(new IdempotencyKey());
  const est = useApiMutation(
    () =>
      api.post<PushAudience>('/v1/admin/push-campaigns/audience', {
        storeId: campaign.storeId,
        segmentIds: campaign.segmentIds,
      }),
    { onSuccess: (r) => setEstimate(r) },
  );
  const send = useApiMutation(
    () => {
      const b = { scheduledAt: later && at ? new Date(at).toISOString() : null, version: campaign.version };
      return api.post<PushCampaign>(`/v1/admin/push-campaigns/${campaign.id}/schedule`, b, {
        idempotencyKey: idem.current.for(JSON.stringify(b)),
      });
    },
    {
      invalidate: [['push-campaigns'], ['push-campaign', campaign.id]],
      success: later ? t('admin.push.scheduledToast') : t('admin.push.queuedToast'),
      silentFieldErrors: true,
      onSuccess: () => setOpen(false),
    },
  );
  const conflict = send.error instanceof ApiError && !send.error.errors.length ? send.error.title : null;
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <Button
          variant="primary"
          disabled={dirty || !campaign.usable}
          onClick={() => {
            idem.current = new IdempotencyKey();
            send.reset();
            setEstimate(null);
            setOpen(true);
            est.mutate(undefined);
          }}
        >
          <Send className="h-4 w-4" aria-hidden />
          {t('admin.push.send')}
        </Button>
        <HelpTip k="push.send" />
      </span>
      {!campaign.usable && <span className="text-sm text-ink-2">{t('admin.push.reviewRequired')}</span>}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('admin.push.sendTitle')}
        description={t('admin.push.sendLead')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={send.isPending || !estimate?.eligible || (later && !at)}
              onClick={() => send.mutate(undefined)}
            >
              {later
                ? t('admin.push.scheduleConfirm')
                : t('admin.push.sendConfirm', { count: estimate?.eligible ?? 0 })}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {!ready && (
            <p className="rounded-md border border-amber/30 bg-amber-tint px-3 py-2 text-sm">
              {t('admin.push.sendSetupWarning')}
            </p>
          )}
          {!estimate ? <Spinner label={t('common.loading')} /> : <EstimateView estimate={estimate} />}
          <fieldset className="space-y-1.5 text-sm">
            <legend className="mb-1 font-medium">{t('admin.campaigns.when')}</legend>
            <label className="flex items-center gap-2">
              <input type="radio" name="push-when" checked={!later} onChange={() => setLater(false)} />
              {t('admin.campaigns.now')}
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="push-when" checked={later} onChange={() => setLater(true)} />
              {t('admin.campaigns.later')}
            </label>
          </fieldset>
          {later && (
            <Field
              label={t('admin.campaigns.scheduledAt')}
              help={<HelpTip k="push.scheduled_at" />}
              error={fieldError(send.error, 'scheduledAt')}
            >
              {(p) => (
                <Input {...p} type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
              )}
            </Field>
          )}
          {[fieldError(send.error, 'segmentIds'), conflict].filter(Boolean).map((m) => (
            <p key={m} role="alert" className="text-sm font-medium text-rose">
              {m}
            </p>
          ))}
        </div>
      </Dialog>
    </>
  );
}

function StatusSection({
  campaign,
  canSend,
  onCancel,
  onResume,
  busy,
}: {
  campaign: PushCampaign;
  canSend: boolean;
  onCancel: () => Promise<unknown>;
  onResume: () => void;
  busy: boolean;
}) {
  const t = useT();
  const { formatDate } = useI18n();
  const p = campaign.progress;
  const done = p ? p.total - p.queued : 0;
  const reason = campaign.pauseReason;
  return (
    <Section title={t('admin.push.statusTitle')} action={<HelpTip k="push.status" />}>
      <div className="space-y-3 text-sm">
        <KeyValue
          items={[
            { label: t('admin.push.statusLabel'), value: t(`admin.push.statuses.${campaign.status}`) },
            ...(campaign.scheduledAt
              ? [{ label: t('admin.campaigns.scheduledAt'), value: formatDate(campaign.scheduledAt) }]
              : []),
            ...(campaign.startedAt
              ? [{ label: t('admin.campaigns.startedAt'), value: formatDate(campaign.startedAt) }]
              : []),
            ...(campaign.finishedAt
              ? [{ label: t('admin.campaigns.finishedAt'), value: formatDate(campaign.finishedAt) }]
              : []),
          ]}
        />
        {p && p.total > 0 && (
          <div className="max-w-xl space-y-1">
            <progress
              className="h-2 w-full accent-[var(--zt-pine)]"
              max={p.total}
              value={done}
              aria-label={t('admin.push.progressLabel')}
            />
            <p className="text-ink-2" aria-live="polite">
              {t('admin.push.progress', {
                done,
                total: p.total,
                sent: p.sent,
                expired: p.expired,
                failed: p.failed,
              })}
            </p>
          </div>
        )}
        {campaign.status === 'paused' && reason && (
          <p role="alert" className="rounded-md border border-rose/30 bg-rose-tint px-3 py-2 text-rose">
            {PAUSE_KEYS.includes(reason)
              ? t(`admin.push.pauseReasons.${reason}`)
              : t('admin.push.pauseReasonOther', { code: reason })}
          </p>
        )}
        {canSend && (
          <div className="flex flex-wrap items-center gap-2">
            {campaign.status === 'paused' && (
              <>
                <Button size="sm" variant="primary" onClick={onResume} disabled={busy}>
                  <Play className="h-4 w-4" aria-hidden />
                  {t('admin.push.resume')}
                </Button>
                <HelpTip k="push.resume" />
              </>
            )}
            {['scheduled', 'sending', 'paused'].includes(campaign.status) && (
              <>
                <ConfirmButton
                  variant="secondary"
                  message={t('admin.push.cancelConfirm')}
                  onConfirm={onCancel}
                  disabled={busy}
                >
                  <Pause className="h-4 w-4" aria-hidden />
                  {t('admin.push.cancel')}
                </ConfirmButton>
                <HelpTip k="push.cancel" />
              </>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

function ReportSection({ campaign }: { campaign: PushCampaign }) {
  const t = useT();
  const money = useMoney();
  const q = useQuery({
    queryKey: ['push-campaign-report', campaign.id],
    queryFn: () => api.get<PushReport>(`/v1/admin/push-campaigns/${campaign.id}/report`),
    refetchInterval: LIVE.includes(campaign.status) ? 5000 : false,
  });
  const r = q.data;
  if (!r) return <Spinner label={t('common.loading')} />;
  const stats: Array<[string, number]> = [
    ['recipients', r.totals.recipients],
    ['sent', r.totals.sent],
    ['expired', r.totals.expired],
    ['failed', r.totals.failed],
    ['skipped', r.totals.skipped],
  ];
  const errors = Object.entries(r.errors).filter(([, n]) => n > 0);
  return (
    <Section
      title={t('admin.push.reportTitle')}
      description={t('admin.push.reportLead')}
      action={<HelpTip k="push.report" />}
    >
      <div className="space-y-6 text-sm">
        {r.purged && <p className="text-ink-2">{t('admin.push.purged')}</p>}
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {stats.map(([k, v]) => (
            <div key={k} className="rounded-md border border-line bg-surface px-3 py-2">
              <dt className="text-ink-3">{t(`admin.push.stats.${k}`)}</dt>
              <dd className="font-cond text-xl font-semibold tabular-nums">{v}</dd>
            </div>
          ))}
        </dl>
        {errors.length > 0 && (
          <div>
            <p className="mb-1 font-medium">{t('admin.push.errorsTitle')}</p>
            <ul className="list-disc space-y-0.5 ps-5 text-ink-2">
              {errors.map(([k, n]) => (
                <li key={k}>
                  {KNOWN_ERRORS.includes(k)
                    ? t(`admin.push.sendErrors.${k}`, { count: n })
                    : t('admin.push.serviceError', { code: k, count: n })}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <p className="mb-1 font-medium">{t('admin.campaigns.ordersTitle')}</p>
          {!r.orders.length ? (
            <p className="text-ink-2">{t('admin.campaigns.noOrders')}</p>
          ) : (
            <ul className="space-y-0.5 text-ink-2">
              {r.orders.map((o) => (
                <li key={o.currency}>
                  {t('admin.campaigns.ordersLine', {
                    orders: o.orders,
                    paid: o.paidOrders,
                    revenue: money(o.revenueMinor, o.currency),
                  })}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-ink-3">{t('admin.push.ordersNote')}</p>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------- Hesabım → Mobil bildirimler

/** Android yönetim uygulaması: kayıtlı cihazlar, bildirim tercihleri (yetkiye göre), sessiz saat, deneme. */
export function MobileNotificationsSection() {
  const t = useT();
  const { formatRelative } = useI18n();
  const devices = useQuery({
    queryKey: ['my-devices'],
    queryFn: () => api.get<StaffDevice[]>('/v1/admin/me/devices'),
  });
  const prefs = useQuery({
    queryKey: ['my-notification-prefs'],
    queryFn: () => api.get<StaffPrefs>('/v1/admin/me/notification-preferences'),
  });
  const [events, setEvents] = useState<string[] | null>(null);
  const [quiet, setQuiet] = useState<{ start: number; end: number } | null | undefined>(undefined);
  const testIdem = useRef(new IdempotencyKey());
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/me/devices/${id}`), {
    invalidate: [['my-devices']],
    success: t('admin.notifications.removed'),
  });
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/me/notification-preferences', {
        events: events ?? [],
        quietHours: quiet === undefined ? (prefs.data?.quietHours ?? null) : quiet,
      }),
    {
      invalidate: [['my-notification-prefs']],
      success: t('admin.notifications.saved'),
      onSuccess: () => {
        setEvents(null);
        setQuiet(undefined);
      },
    },
  );
  const test = useApiMutation(
    () =>
      api.post<{ sent: number; removed: number }>(
        '/v1/admin/me/devices/test',
        {},
        { idempotencyKey: testIdem.current.for('{}') },
      ),
    { success: (r) => t('admin.notifications.testSent', { count: r.sent }) },
  );
  if (!prefs.data || !devices.data) return <Spinner label={t('common.loading')} />;
  const current = events ?? prefs.data.events.filter((e) => e.enabled).map((e) => e.event);
  const q = quiet === undefined ? prefs.data.quietHours : quiet;
  const dirty = events !== null || quiet !== undefined;
  return (
    <Section
      title={t('admin.notifications.title')}
      description={t('admin.notifications.lead')}
      action={<HelpTip k="notifications.section" />}
    >
      <div className="space-y-5">
        {!prefs.data.firebaseConnected && (
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone="amber">{t('integrationStatus.NOT_CONFIGURED')}</Badge>
            {t('admin.notifications.firebaseMissing')}
          </p>
        )}
        <div>
          <p className="mb-2 flex items-center gap-1 text-sm font-medium">
            {t('admin.notifications.devices')}
            {devices.data.length > 0 && <HelpTip k="notifications.device_remove" />}
          </p>
          {!devices.data.length ? (
            <p className="text-sm text-ink-3">{t('admin.notifications.noDevices')}</p>
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {devices.data.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{d.name ?? t('admin.notifications.unnamed')}</p>
                    <p className="text-sm text-ink-3">
                      {t('admin.notifications.deviceLine', {
                        version: d.appVersion ?? '—',
                        seen: formatRelative(d.lastSeenAt),
                      })}
                    </p>
                  </div>
                  <ConfirmButton
                    variant="ghost"
                    message={t('admin.notifications.removeConfirm')}
                    onConfirm={() => remove.mutateAsync(d.id).catch(() => undefined)}
                  >
                    {t('admin.notifications.remove')}
                  </ConfirmButton>
                </li>
              ))}
            </ul>
          )}
        </div>
        <fieldset className="space-y-1.5">
          <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
            {t('admin.notifications.events')}
            <HelpTip k="notifications.events" />
          </legend>
          {prefs.data.events.map((e) => (
            <label key={e.event} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                disabled={!e.allowed}
                checked={e.allowed && current.includes(e.event)}
                onChange={(ev) =>
                  setEvents(ev.target.checked ? [...current, e.event] : current.filter((x) => x !== e.event))
                }
              />
              {t(`admin.notifications.eventNames.${e.event.replace('.', '_')}`)}
              {!e.allowed && <span className="text-ink-3">{t('admin.notifications.noPermission')}</span>}
            </label>
          ))}
        </fieldset>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Switch
              id="staff-quiet"
              checked={!!q}
              onCheckedChange={(v) => setQuiet(v ? { start: 22, end: 8 } : null)}
            />
            <label htmlFor="staff-quiet" className="text-sm font-medium">
              {t('admin.notifications.quietHours')}
            </label>
            <HelpTip k="notifications.quiet_hours" />
          </div>
          {q && (
            <div className="grid max-w-sm grid-cols-2 gap-3">
              <Field label={t('admin.automations.quietStart')}>
                {(p) => (
                  <Select
                    {...p}
                    value={q.start}
                    onChange={(e) => setQuiet({ ...q, start: Number(e.target.value) })}
                  >
                    {HOURS.map((hh) => (
                      <option key={hh} value={hh}>
                        {pad(hh)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('admin.automations.quietEnd')}>
                {(p) => (
                  <Select
                    {...p}
                    value={q.end}
                    onChange={(e) => setQuiet({ ...q, end: Number(e.target.value) })}
                  >
                    {HOURS.map((hh) => (
                      <option key={hh} value={hh}>
                        {pad(hh)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => save.mutate(undefined)} disabled={!dirty || save.isPending}>
            {t('admin.notifications.save')}
          </Button>
          <HelpTip k="notifications.save" />
          <Button
            variant="secondary"
            onClick={() => {
              testIdem.current = new IdempotencyKey();
              test.mutate(undefined);
            }}
            disabled={!devices.data.length || !prefs.data.firebaseConnected || test.isPending}
          >
            <BellRing className="h-4 w-4" aria-hidden />
            {t('admin.notifications.test')}
          </Button>
          <HelpTip k="notifications.test" />
        </div>
      </div>
    </Section>
  );
}
